import React, { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "./SEO";

const HorseRides: React.FC = () => {
  const [activeTab, setActiveTab] = useState("pony");

  const rides = [
    {
      id: "pony",
      title: "Pony Rides",
      subtitle: "For Kids",
      price: "R175",
      duration: "10 minutes",
      ageRange: "Ages 2-8",
      description:
        "Perfect for kids aged 2 to 8, a guided 10-minute stables stroll - great for nervous riders.",
      features: [
        "Ages 2-8 years",
        "10-minute guided ride",
        "Perfect for beginners",
        "Parents can walk beside",
      ],
      image: "/ponyRide1.jpg",
      color: "from-pink-400 to-pink-600",
      bookingLink: "https://secure.activitybridge.com/book?activityid=15937",
    },
    {
      id: "mini",
      title: "Mini Ride",
      subtitle: "For Adults & Kids",
      price: "R300/R400",
      duration: "30 minutes",
      ageRange: "Ages 4+",
      description:
        "Adventure awaits for all ages, starting at 4 years old. Enjoy a 30-minute, 2km trail ride.",
      features: [
        "Ages 4+ years",
        "30-minute trail ride",
        "2km scenic route",
        "Children up to 10 led by guide",
      ],
      image: "/miniRide1.jpg",
      color: "from-blue-400 to-blue-600",
      bookingLink: "https://secure.activitybridge.com/book?activityid=15938",
    },
    {
      id: "standard",
      title: "Standard Ride",
      subtitle: "For Adults",
      price: "R650",
      duration: "60 minutes",
      ageRange: "Ages 12+",
      description:
        "Captivating rides for ages 12+, our 5km trail adventure lasts 60 minutes.",
      features: [
        "Ages 12+ years",
        "60-minute trail ride",
        "5km scenic route",
        "Leisurely walking pace",
      ],
      image: "/standardRide3.jpg",
      color: "from-green-400 to-green-600",
      bookingLink: "https://secure.activitybridge.com/book?activityid=15939",
    },
  ];

  const activeRide = rides.find((ride) => ride.id === activeTab);

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 via-stables-50 to-earth-50">
      <SEO
        title="Horse Rides Pretoria East | Pony, Mini & Standard Rides | The Stables"
        description="Book pony rides for kids (R175), mini rides (R300–R400) and standard trail rides (R650) at The Stables, Pretoria East. Guided rides for all ages and skill levels."
        path="/horse-rides"
        image="https://www.thestableshorserides.co.za/ponyRide1.jpg"
      />
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background first so overlays render on top */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/sunset1.jpg')",
          }}
        ></div>
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-transparent"></div>
        <div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto">
          <h1 className="text-6xl md:text-8xl font-bold mb-6 animate-fade-in">
            Horse Rides
          </h1>
          <p className="text-2xl md:text-3xl mb-8 font-light animate-fade-in-delay">
            Choose your perfect adventure
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center animate-fade-in-delay-2">
            <a
              href="#rides"
              className="bg-stables-600 hover:bg-stables-700 text-white px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300"
            >
              View Rides
            </a>
            <Link
              to="/horse-ride-picnic"
              className="bg-white text-stables-800 hover:bg-stables-100 px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300"
            >
              Add Picnic
            </Link>
          </div>
        </div>
      </section>

      {/* Interactive Ride Selection */}
      <section id="rides" className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            {rides.map((ride) => (
              <button
                key={ride.id}
                onClick={() => setActiveTab(ride.id)}
                className={`px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300 transform hover:scale-105 ${
                  activeTab === ride.id
                    ? "bg-stables-600 text-white shadow-2xl"
                    : "bg-white text-stables-800 hover:bg-stables-100 shadow-lg"
                }`}
              >
                {ride.title}
              </button>
            ))}
          </div>

          {/* Active Ride Display */}
          {activeRide && (
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <div>
                  <h2 className="text-5xl font-bold text-stables-800 mb-2">
                    {activeRide.title}
                  </h2>
                  <p className="text-2xl text-stables-600 mb-6">
                    {activeRide.subtitle}
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-2 sm:gap-6">
                  <div className="text-center p-3 sm:p-6 bg-white rounded-2xl shadow-lg">
                    <div className="text-lg sm:text-3xl font-bold text-stables-600 leading-tight">
                      {activeRide.price}
                    </div>
                    <div className="text-xs sm:text-sm text-stables-700 mt-1">per person</div>
                  </div>
                  <div className="text-center p-3 sm:p-6 bg-white rounded-2xl shadow-lg">
                    <div className="text-lg sm:text-3xl font-bold text-stables-600 leading-tight">
                      {activeRide.duration}
                    </div>
                    <div className="text-xs sm:text-sm text-stables-700 mt-1">duration</div>
                  </div>
                  <div className="text-center p-3 sm:p-6 bg-white rounded-2xl shadow-lg">
                    <div className="text-lg sm:text-3xl font-bold text-stables-600 leading-tight">
                      {activeRide.ageRange}
                    </div>
                    <div className="text-xs sm:text-sm text-stables-700 mt-1">age range</div>
                  </div>
                </div>

                <p className="text-xl text-stables-700 leading-relaxed">
                  {activeRide.description}
                </p>

                <div className="grid grid-cols-2 gap-4">
                  {activeRide.features.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center p-4 bg-white rounded-xl shadow-md"
                    >
                      <div className="w-3 h-3 bg-stables-600 rounded-full mr-3"></div>
                      <span className="text-stables-700 font-medium">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                <a
                  href={activeRide.bookingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-stables-600 hover:bg-stables-700 text-white px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300"
                >
                  Book This Ride
                </a>
              </div>

              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-stables-600 to-stables-700 rounded-3xl sm:rotate-3 sm:group-hover:rotate-6 transition-transform duration-300"></div>
                <img
                  src={activeRide.image}
                  alt={activeRide.title}
                  className="relative rounded-3xl shadow-2xl sm:-rotate-3 sm:group-hover:-rotate-6 transition-transform duration-300 w-full aspect-[4/3] object-cover"
                />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* All Rides Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-stables-800 mb-6">
              All Our Rides
            </h2>
            <p className="text-xl text-stables-700 max-w-3xl mx-auto">
              From gentle pony rides for toddlers to adventurous trail rides for
              adults
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {rides.map((ride) => (
              <div key={ride.id} className="group relative">
                <div
                  className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"
                  style={{
                    background: `linear-gradient(135deg, ${ride.color})`,
                  }}
                ></div>
                <div className="relative bg-white rounded-3xl p-8 shadow-xl group-hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-2">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-stables-800 mb-2">
                      {ride.title}
                    </h3>
                    <p className="text-stables-600">{ride.subtitle}</p>
                  </div>

                  <div className="text-center mb-6">
                    <div className="text-4xl font-bold text-stables-600 mb-2">
                      {ride.price}
                    </div>
                    <div className="text-sm text-stables-700">
                      {ride.duration} • {ride.ageRange}
                    </div>
                  </div>

                  <p className="text-stables-700 mb-6 text-center">
                    {ride.description}
                  </p>

                  <div className="space-y-3 mb-8">
                    {ride.features.slice(0, 2).map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <div className="w-2 h-2 bg-stables-600 rounded-full mr-3"></div>
                        <span className="text-sm text-stables-700">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  <a
                    href={ride.bookingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full block text-center bg-stables-600 hover:bg-stables-700 text-white py-4 rounded-xl font-semibold transition-colors duration-300"
                  >
                    Book Now
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="py-20 bg-gradient-to-r from-stables-600 to-stables-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        <div className="relative z-10 max-w-5xl mx-auto text-center px-4">
          <h2 className="text-5xl font-bold mb-6">Ready for Your Adventure?</h2>
          <p className="text-2xl mb-12 max-w-3xl mx-auto">
            Book your horse riding experience today and create unforgettable
            memories with your family.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a
              href="#rides"
              className="bg-white text-stables-800 hover:bg-stables-100 px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300"
            >
              Choose Your Ride
            </a>
            <Link
              to="/horse-ride-picnic"
              className="bg-stables-800 hover:bg-stables-900 text-white px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300"
            >
              View Picnic Options
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HorseRides;
